import fetch from 'cross-fetch';
import {
  ApolloClient,
  InMemoryCache,
  HttpLink,
  ApolloLink,
  from,
  fromPromise,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';

import * as tokenStore from 'src/utils/auth/tokenStore';
import { GRAPHQL_URL } from 'src/utils/auth/config';

const url = GRAPHQL_URL;

const httpLink = new HttpLink({
  uri: url,
  fetch,
});

/**
 * Session handler - if we have a session token in localStorage, add it to the GraphQL request as a Session header.
 *
 * @ref https://github.com/wp-graphql/wp-graphql-woocommerce/pull/88
 */
export const wooSessionMiddleware = new ApolloLink((operation, forward) => {
  // Don't run this while Gatsby is building. Window is not defined
  // @ref: https://www.gatsbyjs.org/docs/debugging-html-builds/
  if (typeof window === 'undefined') {
    return forward(operation);
  }

  /**
   * If session data exists in local storage, set value as session header.
   */
  const session = window.localStorage.getItem('woo-session');
  if (session) {
    operation.setContext(({ headers = {} }) => ({
      headers: {
        ...headers,
        'woocommerce-session': `Session ${session}`,
      },
    }));
  }

  return forward(operation);
});

/**
 * Session handler - catch the incoming session token and store it in localStorage, for future GraphQL requests.
 *
 * @ref https://github.com/wp-graphql/wp-graphql-woocommerce/pull/88
 */
export const wooSessionAfterware = new ApolloLink((operation, forward) => {
  // Don't run this while Gatsby is building. Window is not defined
  // @ref: https://www.gatsbyjs.org/docs/debugging-html-builds/
  if (typeof window === 'undefined') {
    return forward(operation);
  }

  return forward(operation).map(response => {
    const context = operation.getContext();

    const {
      response: { headers },
    } = context;

    const session = headers.get('woocommerce-session');

    // Bail if no session was sent
    if (!session) {
      return response;
    }

    // Bail if we already have the session
    if (window.localStorage.getItem('woo-session') === session) {
      return response;
    }

    // Set WC session to localStorage
    window.localStorage.setItem('woo-session', session);

    return response;
  });
});

/**
 * Attaches the customer's bearer token. Kept separate from wooSessionMiddleware so the
 * anonymous cart path is byte-for-byte unchanged: the two links never touch each
 * other's header, and an anonymous request behaves exactly as it did before accounts
 * existed.
 */
export const authMiddleware = new ApolloLink((operation, forward) => {
  if (typeof window === 'undefined') {
    return forward(operation);
  }

  const token = tokenStore.getAuthToken();

  if (token) {
    operation.setContext(({ headers = {} }) => ({
      headers: {
        ...headers,
        Authorization: `Bearer ${token}`,
      },
    }));
  }

  return forward(operation);
});

const AUTH_ERROR_PATTERN = /expired|invalid[- ]jwt|not authoriz|unauthenticated/i;

const isAuthError = (graphQLErrors: any, networkError: any): boolean => {
  const status = networkError?.statusCode;

  if (status === 401 || status === 403) {
    return true;
  }

  return Boolean(
    graphQLErrors?.some(
      (error: any) =>
        error?.extensions?.category === 'user' &&
        AUTH_ERROR_PATTERN.test(error?.message ?? '')
    )
  );
};

/**
 * Refreshes an expired token once and replays the request.
 *
 * Placed FIRST in the chain: onError's `forward` re-enters the chain after the link
 * that errored, so only from here does the replay pass back through authMiddleware and
 * pick up the new token.
 *
 * Two guards matter. Auth mutations are skipped by operation name, because a failed
 * login returns `extensions.category: "user"` exactly like an expired token — without
 * that check a wrong password would silently trigger a refresh and re-submit the login.
 * And `__authRetried` bounds each operation to a single retry, so a persistently
 * rejected token can never loop.
 */
export const errorLink = onError(
  ({ graphQLErrors, networkError, operation, forward }) => {
    if (typeof window === 'undefined') {
      return;
    }

    if (tokenStore.isAuthOperation(operation.operationName)) {
      return;
    }

    if (!tokenStore.getAuthToken()) {
      return;
    }

    if (!isAuthError(graphQLErrors, networkError)) {
      return;
    }

    if (operation.getContext().__authRetried) {
      tokenStore.signOut();

      return;
    }

    operation.setContext({ __authRetried: true });

    return fromPromise(
      tokenStore.refresh().then(token => {
        if (!token) {
          tokenStore.signOut();

          throw new Error('Sesija baigėsi. Prisijunkite iš naujo.');
        }

        return token;
      })
    ).flatMap(() => forward(operation));
  }
);

/**
 * Exported so tests can build a cache configured exactly like production's. Without
 * this, MockedProvider's default cache lacks the pagination policy and `fetchMore`
 * behaves differently in tests than in the app.
 */
export const createCache = (): InMemoryCache =>
  new InMemoryCache({
    typePolicies: {
      Cart: {
        keyFields: ['total'], // cart doesn't have an id, so per docs we need to track it somehow to make cache work as expected. This warning came up during coupons on the cart page
      },
      Order: {
        keyFields: ['databaseId'],
      },
      Customer: {
        fields: {
          orders: {
            // Keyed on `where` so a status-filtered list doesn't merge into the
            // unfiltered one. NOT relayStylePagination: that merges `edges`, and the
            // query uses WPGraphQL's `nodes` shorthand, so it would silently replace
            // each page instead of appending.
            keyArgs: ['where'],
            merge(existing: any, incoming: any, { args }: any) {
              // A fresh load (no cursor) replaces; a fetchMore appends.
              if (!existing || !args?.after) {
                return incoming;
              }

              return {
                ...incoming,
                nodes: [...(existing.nodes ?? []), ...(incoming.nodes ?? [])],
              };
            },
          },
        },
      },
    },
  });

export const client = new ApolloClient({
  cache: createCache(),
  link: from([
    errorLink,
    authMiddleware,
    wooSessionMiddleware,
    wooSessionAfterware,
    httpLink,
  ]),
});
